import { useStore, useStoreInventories } from '@gozoki/api';
import { frenchFormat } from '@gozoki/tools';
import { Dialog, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../components/communication/Toast';
import { DashboardCard, ProductCard } from '../../utils/dashboard/dashboardCards';
import { FourDatePicker } from '../../utils/dashboard/fourDatePicker';
import { timeRanges } from '../../utils/dashboard/timeRanges';
import { prepareReceptionDataForPeriod } from '../../utils/excel/prepareReceptionData';
import { writeReceptionFile } from '../../utils/excel/writeReceptionXlsx';
import { writeArticlesDetailsFile } from '../../utils/excel/writeStoresFiles';
import { aggregateProducts } from '../../utils/inventory/aggregateProducts';
import { generateReceptionPdf } from '../../utils/pdf/writeReceptionPdf';
import { fonts } from '../../utils/theme/fonts.theme';
import { InventoryStatsDetailLine, type InventoryStats } from './InventoryStatsDetailLine';

export interface InventoriesStatsTabProps {
    shopId: number;
}

export const InventoriesStatsTab = (props: InventoriesStatsTabProps) => {
    const dateNow = new Date();

    const [Toast, showToast] = useToast();

    const { shopId } = props;
    const { data: store } = useStore(shopId);
    const { data: inventories, isLoading: isLoadingInventories } = useStoreInventories(shopId);

    const [globalDatas, setGlobalDatas] = useState<Record<string, InventoryStats>>({});

    // ---------------------- DATE RANGE ---------------------- //
    const [currentTimeRange, setCurrentTimeRange] = useState<number>(-1);
    const [currentDateStart, setCurrentDateStart] = useState<Date>(dateNow);
    const [currentDateEnd, setCurrentDateEnd] = useState<Date>(dateNow);

    const dateState = useMemo(
        () => ({
            timeRanges: {
                all: timeRanges.slice(0, 3),
                current: currentTimeRange,
                set: setCurrentTimeRange,
            },
            currentDate: {
                start: currentDateStart,
                setStart: setCurrentDateStart,
                end: currentDateEnd,
                setEnd: setCurrentDateEnd,
            },
        }),
        [currentTimeRange, currentDateStart, currentDateEnd]
    );

    const currentInventories = useMemo(() => {
        if (!inventories) return [];
        return inventories
            .filter((inventory) => {
                const creationDate = new Date(inventory.createdAt);
                const isWithinDateRange =
                    creationDate.getTime() >= currentDateStart.getTime() &&
                    (currentTimeRange === 1
                        ? creationDate.getTime() <= currentDateEnd.getTime()
                        : true);
                return isWithinDateRange && inventory.status === 'validated';
            })
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }, [inventories, currentDateStart, currentDateEnd, currentTimeRange]);

    const { productsWithCount: allSoldProductsDatas, ca: allSoldArticlesCa } = useMemo(() => {
        const allSoldArticlesMap = new Map();
        Object.values(globalDatas)
            .flatMap((inventoryStats) => inventoryStats.sold)
            .forEach((article) => {
                allSoldArticlesMap.set(article.checkpointArticleRef, article);
            });

        const allSoldArticles = Array.from(allSoldArticlesMap.values());
        const ca = allSoldArticles.reduce((acc, article) => acc + (article.soldPrice ?? 0), 0);
        const productsWithCount = aggregateProducts(Array.from(allSoldArticles), 'sellPrice');
        return { productsWithCount, ca };
    }, [globalDatas]);

    const allDeliveredProductsDatas = useMemo(() => {
        const allDeliveredArticlesMap = new Map();
        Object.values(globalDatas)
            .flatMap((inventoryStats) => inventoryStats.delivered)
            .forEach((article) => {
                allDeliveredArticlesMap.set(article.checkpointArticleRef, article);
            });
        const allDeliveredArticles = Array.from(allDeliveredArticlesMap.values());
        const productsWithCount = aggregateProducts(Array.from(allDeliveredArticles), 'sellPrice');
        return productsWithCount;
    }, [globalDatas]);

    const { productsWithCount: allDestroyedProductsDatas, ca: allDestroyedArticlesCa } =
        useMemo(() => {
            const allDestroyedArticlesMap = new Map();
            Object.values(globalDatas)
                .flatMap((inventoryStats) => inventoryStats.destroyed)
                .forEach((article) => {
                    allDestroyedArticlesMap.set(article.checkpointArticleRef, article);
                });
            const allDestroyedArticles = Array.from(allDestroyedArticlesMap.values());
            const ca = allDestroyedArticles.reduce(
                (acc, article) => acc + (article.product.price ?? 0),
                0
            );
            const productsWithCount = aggregateProducts(
                Array.from(allDestroyedArticles),
                'sellPrice'
            );
            return { productsWithCount, ca };
        }, [globalDatas]);

    const { productsWithCount: allDefectiveProductsDatas, ca: allDefectiveArticlesCa } =
        useMemo(() => {
            const allDefectiveArticlesMap = new Map();
            Object.values(globalDatas)
                .flatMap((inventoryStats) => inventoryStats.defective)
                .forEach((article) => {
                    allDefectiveArticlesMap.set(article.checkpointArticleRef, article);
                });
            const allDefectiveArticles = Array.from(allDefectiveArticlesMap.values());
            const ca = allDefectiveArticles.reduce(
                (acc, article) => acc + (article.product.price ?? 0),
                0
            );
            const productsWithCount = aggregateProducts(
                Array.from(allDefectiveArticles),
                'sellPrice'
            );
            return { productsWithCount, ca };
        }, [globalDatas]);

    const allErroredProductsDatas = useMemo(() => {
        const allErroredArticlesMap = new Map();
        Object.values(globalDatas)
            .flatMap((inventoryStats) => inventoryStats.errored)
            .forEach((article) => {
                allErroredArticlesMap.set(article.checkpointArticleRef, article);
            });
        const allErroredArticles = Array.from(allErroredArticlesMap.values());
        const productsWithCount = aggregateProducts(Array.from(allErroredArticles), 'sellPrice');
        return productsWithCount;
    }, [globalDatas]);

    const { productsWithCount: allStolenProductsDatas, ca: allStolenArticlesCa } = useMemo(() => {
        const allStolenArticlesMap = new Map();
        Object.values(globalDatas)
            .flatMap((inventoryStats) => inventoryStats.stolen)
            .forEach((article) => {
                allStolenArticlesMap.set(article.checkpointArticleRef, article);
            });
        const allStolenArticles = Array.from(allStolenArticlesMap.values());
        const productsWithCount = aggregateProducts(Array.from(allStolenArticles), 'sellPrice');
        const ca = allStolenArticles.reduce(
            (acc, article) => acc + (article.product.price ?? 0),
            0
        );
        return { productsWithCount, ca };
    }, [globalDatas]);

    // -------------------- display and download details -------------------- //
    const [showDetailsProducts, setShowDetailsProducts] = useState<boolean>(false);
    const [productsToShow, setProductsToShow] = useState<
        'sold' | 'delivered' | 'destroyed' | 'defective' | 'stolen' | 'errored'
    >('sold');
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const detailsLabel = useMemo(() => {
        if (productsToShow === 'sold') return 'Produits vendus';
        if (productsToShow === 'delivered') return 'Produits livrés';
        if (productsToShow === 'destroyed') return 'Produits cassés';
        if (productsToShow === 'defective') return 'Produits défectueux';
        if (productsToShow === 'stolen') return 'Produits volés (ou perdus)';
        return '';
    }, [productsToShow]);

    const productsDataToShow = useMemo(() => {
        if (productsToShow === 'sold') return allSoldProductsDatas;
        if (productsToShow === 'delivered') return allDeliveredProductsDatas;
        if (productsToShow === 'destroyed') return allDestroyedProductsDatas;
        if (productsToShow === 'defective') return allDefectiveProductsDatas;
        if (productsToShow === 'stolen') return allStolenProductsDatas;
        return [];
    }, [
        productsToShow,
        allSoldProductsDatas,
        allDeliveredProductsDatas,
        allDestroyedProductsDatas,
        allDefectiveProductsDatas,
        allStolenProductsDatas,
    ]);

    const productsToShowCount = useMemo(() => {
        if (productsToShow === 'sold') {
            return allSoldProductsDatas.reduce((acc, product) => acc + product.count, 0);
        }
        if (productsToShow === 'delivered') {
            return allDeliveredProductsDatas.reduce((acc, product) => acc + product.count, 0);
        }
        if (productsToShow === 'defective') {
            return allDefectiveProductsDatas.reduce((acc, product) => acc + product.count, 0);
        }
        if (productsToShow === 'destroyed') {
            return allDestroyedProductsDatas.reduce((acc, product) => acc + product.count, 0);
        }
        if (productsToShow === 'stolen') {
            return allStolenProductsDatas.reduce((acc, product) => acc + product.count, 0);
        }
        return 0;
    }, [
        productsToShow,
        allSoldProductsDatas,
        allDeliveredProductsDatas,
        allDestroyedProductsDatas,
        allDefectiveProductsDatas,
        allStolenProductsDatas,
    ]);

    const productsToShowCa = useMemo(() => {
        if (productsToShow === 'sold') {
            return allSoldProductsDatas.reduce((acc, product) => acc + product.ca, 0);
        }
        if (productsToShow === 'delivered') {
            return allDeliveredProductsDatas.reduce((acc, product) => acc + product.ca, 0);
        }
        if (productsToShow === 'destroyed') {
            return allDestroyedProductsDatas.reduce((acc, product) => acc + product.ca, 0);
        }
        if (productsToShow === 'stolen') {
            return allStolenProductsDatas.reduce((acc, product) => acc + product.ca, 0);
        }
        if (productsToShow === 'defective') {
            return allDefectiveProductsDatas.reduce((acc, product) => acc + product.ca, 0);
        }
        return 0;
    }, [
        productsToShow,
        allSoldProductsDatas,
        allDeliveredProductsDatas,
        allDestroyedProductsDatas,
        allStolenProductsDatas,
        allDefectiveProductsDatas,
    ]);

    // Memoized values for reception exports (PDF and XLSX)
    const inventoryDates = useMemo(
        () => currentInventories.map((inventory) => DateTime.fromISO(inventory.createdAt)),
        [currentInventories]
    );

    const receptionDate = useMemo(() => {
        if (currentTimeRange === 1 && currentDateEnd) {
            return DateTime.fromJSDate(currentDateEnd);
        }
        if (inventoryDates.length > 0) {
            return inventoryDates[inventoryDates.length - 1];
        }
        return DateTime.now();
    }, [currentTimeRange, currentDateEnd, inventoryDates]);

    const allDeliveredArticles = useMemo(
        () => Object.values(globalDatas).flatMap((inventoryStats) => inventoryStats.delivered),
        [globalDatas]
    );

    const deliveredProductsData = useMemo(
        () => aggregateProducts(allDeliveredArticles, 'price'),
        [allDeliveredArticles]
    );

    const handleDownloadPdf = useCallback(async () => {
        if (!store) return;
        setIsGeneratingPdf(true);
        try {
            await generateReceptionPdf(deliveredProductsData, store, receptionDate, inventoryDates);
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast({
                message: 'Erreur lors de la génération du PDF. Veuillez réessayer.',
                severity: 'error',
            });
        } finally {
            setIsGeneratingPdf(false);
        }
    }, [store, showToast, deliveredProductsData, receptionDate, inventoryDates]);

    const handleDownloadCsv = useCallback(
        async (mode: 'delivered' | 'destroyed' | 'stolen' | 'sold' | 'defective' | 'errored') => {
            if (mode === 'delivered') {
                if (!store) return;
                setIsGeneratingPdf(true);
                try {
                    const productsWithCountAndNeeded = await prepareReceptionDataForPeriod(
                        deliveredProductsData,
                        store,
                        inventoryDates
                    );

                    await writeReceptionFile(
                        productsWithCountAndNeeded,
                        store.label,
                        receptionDate.toFormat('yyyy-MM-dd'),
                        store.label
                    );
                } catch (error) {
                    console.error('Error generating CSV:', error);
                    showToast({
                        message: 'Erreur lors de la génération du CSV. Veuillez réessayer.',
                        severity: 'error',
                    });
                } finally {
                    setIsGeneratingPdf(false);
                }
                return;
            }

            // For other modes, use the simple Excel export with a configuration object
            const exportConfig = {
                destroyed: { data: allDestroyedProductsDatas, label: 'Produits cassés' },
                stolen: { data: allStolenProductsDatas, label: 'Produits volés (ou perdus)' },
                sold: { data: allSoldProductsDatas, label: 'Produits vendus' },
                defective: { data: allDefectiveProductsDatas, label: 'Produits défectueux' },
                errored: {
                    data: allErroredProductsDatas,
                    label: 'Produits en erreur (réapparus entre temps)',
                },
            };

            const config = exportConfig[mode as Exclude<typeof mode, 'delivered'>];

            if (!config?.data) {
                return;
            }

            const fileLabel = `${config.label.toLowerCase().replace(/\s+/g, '_')}.xlsx`;
            writeArticlesDetailsFile(config.data, fileLabel, store?.label);
        },
        [
            allDefectiveProductsDatas,
            allDestroyedProductsDatas,
            allErroredProductsDatas,
            allSoldProductsDatas,
            allStolenProductsDatas,
            store,
            showToast,
            deliveredProductsData,
            receptionDate,
            inventoryDates,
        ]
    );

    const setInventoryStats = useCallback((inventoryId: number, inventoryStats: InventoryStats) => {
        // Vérification que les données sont valides avant de les stocker
        if (!inventoryStats.delivered.length) {
            return;
        }

        setGlobalDatas((prev) => {
            // Créer une copie profonde pour éviter les références partagées
            const newInventoryStats = {
                delivered: [...inventoryStats.delivered],
                destroyed: [...inventoryStats.destroyed],
                stolen: [...inventoryStats.stolen],
                errored: [...inventoryStats.errored],
                sold: [...inventoryStats.sold],
                defective: [...inventoryStats.defective],
            };

            return { ...prev, [inventoryId]: newInventoryStats };
        });
    }, []);

    const destroyedArticlesPercentage = useMemo(() => {
        const ratio = allDestroyedArticlesCa / allSoldArticlesCa;
        const percentage = ratio * 100;
        return percentage > 0 ? `${percentage.toFixed(2)}%` : undefined;
    }, [allDestroyedArticlesCa, allSoldArticlesCa]);

    const defectiveArticlesPercentage = useMemo(() => {
        const ratio = allDefectiveArticlesCa / allSoldArticlesCa;
        const percentage = ratio * 100;
        return percentage > 0 ? `${percentage.toFixed(2)}%` : undefined;
    }, [allDefectiveArticlesCa, allSoldArticlesCa]);

    const stolenArticlesPercentage = useMemo(() => {
        const ratio = allStolenArticlesCa / allSoldArticlesCa;
        const percentage = ratio * 100;
        return percentage > 0 ? `${percentage.toFixed(2)}%` : undefined;
    }, [allStolenArticlesCa, allSoldArticlesCa]);

    useEffect(() => {
        setGlobalDatas({});
    }, [currentInventories]);

    // Determine if we're still loading data
    // Loading if: inventories are loading OR if we have inventories but no data yet
    const isLoadingData = useMemo(() => {
        if (isLoadingInventories) return true;
        if (currentInventories.length === 0) return false; // No inventories to load
        // If we have inventories but no global data yet, we're still loading
        return Object.keys(globalDatas).length !== currentInventories.length;
    }, [isLoadingInventories, currentInventories.length, globalDatas]);

    return (
        <div style={fonts.roboto}>
            <Toast />
            <Dialog
                sx={fonts.roboto}
                open={showDetailsProducts}
                onClose={() => setShowDetailsProducts(false)}
            >
                <div
                    style={{
                        minWidth: 500,
                        minHeight: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        style={fonts.pageSubtitle}
                        sx={{ textAlign: 'center', marginBottom: 2, marginTop: 2 }}
                    >
                        {detailsLabel}
                    </Typography>

                    <div style={{ maxHeight: 950, overflow: 'auto' }}>
                        {productsDataToShow?.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                count={product.count}
                                ca={product.ca}
                                mode="count"
                                maxCount={productsDataToShow[0].count}
                                maxCa={productsDataToShow[0].ca}
                                totalCount={productsToShowCount}
                                totalCa={productsToShowCa}
                            />
                        ))}
                    </div>
                </div>
            </Dialog>
            <Typography
                sx={{ paddingLeft: 2, paddingTop: 1 }}
                style={fonts.pageSubtitle}
                flexGrow={1}
            >
                Sur la période sélectionnée
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'row', paddingBottom: 20 }}>
                {/* <DashboardCard label="" bigValue="" /> */}
                <DashboardCard
                    label="Produits vendus"
                    bigValue={`${frenchFormat(
                        allSoldProductsDatas.reduce((acc, product) => acc + product.count, 0)
                    )}`}
                    details={() => {
                        setProductsToShow('sold');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('sold');
                        handleDownloadCsv('sold');
                    }}
                    isLoading={isLoadingData}
                />
                <DashboardCard label="" bigValue="" />
                <DashboardCard
                    label="Produits livrés"
                    bigValue={`${frenchFormat(
                        allDeliveredProductsDatas.reduce((acc, product) => acc + product.count, 0)
                    )}`}
                    details={() => {
                        setProductsToShow('delivered');
                        setShowDetailsProducts(true);
                    }}
                    downloadPdf={() => {
                        setProductsToShow('delivered');
                        handleDownloadPdf();
                    }}
                    downloadCsv={() => {
                        setProductsToShow('delivered');
                        handleDownloadCsv('delivered');
                    }}
                    isDownloading={isGeneratingPdf}
                    isLoading={isLoadingData}
                />
                <DashboardCard
                    label="Produits cassés"
                    bigValue={destroyedArticlesPercentage ?? '0 %'}
                    details={() => {
                        setProductsToShow('destroyed');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('destroyed');
                        handleDownloadCsv('destroyed');
                    }}
                    smallValue={`${frenchFormat(
                        allDestroyedProductsDatas.reduce((acc, product) => acc + product.count, 0)
                    )} UVC`}
                    smallOrange={
                        !!`${frenchFormat(
                            allDestroyedProductsDatas.reduce(
                                (acc, product) => acc + product.count,
                                0
                            )
                        )} UVC`
                    }
                    isLoading={isLoadingData}
                />
                <DashboardCard
                    label="Produits volés (ou perdus)"
                    bigValue={stolenArticlesPercentage ?? '0 %'}
                    details={() => {
                        setProductsToShow('stolen');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('stolen');
                        handleDownloadCsv('stolen');
                    }}
                    smallValue={`${frenchFormat(
                        allStolenProductsDatas.reduce((acc, product) => acc + product.count, 0)
                    )} UVC`}
                    smallOrange={
                        !!`${frenchFormat(
                            allStolenProductsDatas.reduce((acc, product) => acc + product.count, 0)
                        )} UVC`
                    }
                    isLoading={isLoadingData}
                />
                <DashboardCard
                    label="Produits défectueux"
                    bigValue={defectiveArticlesPercentage ?? '0 %'}
                    details={() => {
                        setProductsToShow('defective');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('defective');
                        handleDownloadCsv('defective');
                    }}
                    smallValue={`${frenchFormat(
                        allDefectiveProductsDatas.reduce((acc, product) => acc + product.count, 0)
                    )} UVC`}
                    smallOrange={
                        !!`${frenchFormat(
                            allDefectiveProductsDatas.reduce(
                                (acc, product) => acc + product.count,
                                0
                            )
                        )} UVC`
                    }
                    isLoading={isLoadingData}
                />

                <DashboardCard
                    label="Produits en erreur (réapparus entre temps)"
                    bigValue={`${frenchFormat(
                        allErroredProductsDatas.reduce((acc, product) => acc + product.count, 0)
                    )}`}
                    details={() => {
                        setProductsToShow('errored');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('errored');
                        handleDownloadCsv('errored');
                    }}
                    isLoading={isLoadingData}
                />
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', margin: 20 }}>
                <FourDatePicker dateState={dateState} />
            </div>
            {currentInventories.map((inventory) => (
                <InventoryStatsDetailLine
                    key={inventory.id}
                    inventoryId={inventory.id}
                    setInventoryStats={setInventoryStats}
                    globalDatas={globalDatas}
                    store={store}
                />
            ))}
        </div>
    );
};
